import { Injectable } from '@angular/core';
import { UserInfo, UserType } from 'qp-store';
import { ProgramVm, ResourceVm } from 'qp-ui-interface';
import { NewrelicService } from 'src/app/analytics/newrelic/service/newrelic.service';
import { UnivisionEntitlement } from '../models/Entitlements';
import { AdobeService } from '../services/adobe/adobe.service';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { QPATService } from '../services/qpat/qpat.service';
import { isLoggedIn } from '../utils/LoginUtils';

const anonymousMetadata: UserInfo = {
  userID: undefined,
  silentLogin: false,
  isNew: false,
  loginStatus: 'Anonymous',
  baseSubscription: null,
  loginMethod: undefined,
  userCategory: undefined,
  otpCalls: undefined,
  userType: UserType.ANONYMOUS,
};

@Injectable({
  providedIn: 'root',
})
export class EntitlementService {
  constructor(
    private adobeService: AdobeService,
    private localStorage: LocalStorageService,
    private qpatService: QPATService,
    private newrelic: NewrelicService
  ) {}

  /**
   * Validate if the user is enttitled to see the resource
   *
   * @param subscriptionPackage
   * @returns
   */
  async isUserEntitledResource(resource: ResourceVm): Promise<boolean> {
    const packages = resource.entitlement[0].subscriptionPackage;
    return this.isUserEntitled(packages);
  }

  /**
   * Validate if the user is enttitled to see the epg program
   *
   * @param subscriptionPackage
   * @returns
   */
  async isUserEntitledProgram(resource: ProgramVm): Promise<boolean> {
    const packages = resource.entitlement[0].subscriptionPackage;
    return this.isUserEntitled(packages);
  }

  /**
   * Validate if the user is enttitled to see the content based in the subscription package
   *
   * @param subscriptionPackage
   * @returns
   */
  async isUserEntitled(subscriptionPackage: string[]): Promise<boolean> {
    // Check dtc vip user
    const isVipUser = await this.isVipUser();
    if (isVipUser) return true;
    const userEntitledPackagesNames = await this.getUserEntitlementPackages();
    // Check for free entitlements
    if (this.isContentFree(subscriptionPackage)) return true;
    // If the user is logged in
    if (isLoggedIn(this.localStorage) && userEntitledPackagesNames && userEntitledPackagesNames.length > 0) {
      // Return if at least one package of user entitlement is included in the resource subscription package
      return userEntitledPackagesNames.some((element) => subscriptionPackage.includes(element));
    }

    return false; // Return false if not logged in
  }

  isContentFree(contentPackages: string[]): boolean {
    // Check for free entitlements
    if (
      contentPackages.includes(UnivisionEntitlement.FREE_DTC) ||
      contentPackages.includes(UnivisionEntitlement.FREE_TVE) ||
      contentPackages.includes(UnivisionEntitlement.FREE)
    ) {
      return true;
    }
    return false;
  }

  async getUserEntitlementPackages(): Promise<string[]> {
    if (isLoggedIn(this.localStorage)) {
      const qpat = this.qpatService.getQPAT();
      if (!qpat) return [UnivisionEntitlement.BLOCKED];
      if (this.adobeService.fullEntitlementPackages) return this.adobeService.fullEntitlementPackages;
    }
    return [UnivisionEntitlement.FREE_TVE];
  }

  async isVipUser(): Promise<boolean> {
    return false;
  }

  /**
   * Set user info in store
   */
  public setNewrelicUserInfo(): void {
    let userInfo: UserInfo = {} as UserInfo;

    if (isLoggedIn(this.localStorage)) {
      const isSubscribed = this.adobeService.entitlementPackages?.length > 0;
      userInfo = {
        userID: this.adobeService.userId,
        silentLogin: true,
        userType: isSubscribed ? UserType.SUBSCRIBED : UserType.REGISTERED,
        isNew: false,
        loginStatus: 'Logged',
        baseSubscription: isSubscribed ? this.adobeService.entitlementPackages?.join(',') : null,
        loginMethod: undefined,
        userCategory: undefined,
        otpCalls: undefined,
      };
    } else {
      userInfo = anonymousMetadata;
    }

    this.newrelic.store.setUserInfo(userInfo);
  }
}
